import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api.js';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { 
  DollarSign, XCircle, Plus, Trash2, Send, 
  User, CheckCircle, AlertCircle, Info, Printer, Layers, Mail 
} from 'lucide-react';
import ItemsCotizacionSection from './Tramites/ItemsCotizacionSection.jsx';
import CuantiasCotizacionSection from './Tramites/CuantiasCotizacionSection.jsx';
import { handleImprimirCotizacionTramite } from '../utils/printTramiteCotizacion.js';
import HistorialCotizacionesModal from './Tramites/HistorialCotizacionesModal.jsx';



const DEFAULT_ITEMS = [
  "BASES",
  "CONTRATOS SIN CUANTÍA",
  "CUANTÍA PROPIEDAD/PROPIEDAD AGUAS/ COMERCIO",
  "CUANTÍA HIPOTECA/GRAVAMEN",
  "PROHIBICIONES/INTERDICCIONES",
  "RENUNCIAS",
  "ALZAMIENTOS",
  "NOTAS",
  "AGREGADO POSESIÓN EFECTIVA",
  "BASES HERENCIAS",
  "CUANTÍA HERENCIAS",
  "TESTAMENTO",
  "DOCUMENTOS AGREGADOS",
  "SENTENCIA MINERA",
  "CERTIFICACIÓN DIGITAL"
];

export default function CotizacionTramiteModal({ tramite, onClose, onUpdate }) {
  const [isGeneratingNew, setIsGeneratingNew] = useState(false);

  const originalStateLocked = ['Pagado', 'Completado', 'Pendiente Pago', 'Comprobante Pago Recibido'].includes(tramite.estado?.nombre);
  const originalStateFinalized = ['Pagado', 'Completado'].includes(tramite.estado?.nombre);

  const isQuoteLocked = originalStateLocked && !isGeneratingNew;
  const isFinalized = originalStateFinalized && !isGeneratingNew;
  // Para los items de la cotización
  const [items, setItems] = useState(DEFAULT_ITEMS.map(desc => ({ descripcion: desc, cantidad: 1, precio_unitario: 0, is_custom: false, is_cuantia: false })));
  const [cuantias, setCuantias] = useState([]);
  const [cajaUsers, setCajaUsers] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedCajaId, setSelectedCajaId] = useState('');
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(tramite.asignado_a || '');
  const [descuentoGlobalMonto, setDescuentoGlobalMonto] = useState(0);
  const [descuentoGlobalTipo, setDescuentoGlobalTipo] = useState('fijo');
  const [observaciones, setObservaciones] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendingMail, setResendingMail] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [cotizacionesHistory, setCotizacionesHistory] = useState([]);
  const [viewingVersionId, setViewingVersionId] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const hasExistingQuote = Boolean(
    (cotizacionesHistory && cotizacionesHistory.length > 0) || 
    (tramite.cotizacion && tramite.cotizacion.id)
  );

  const handleReenviarCorreo = async () => {
    if (!tramite.correo) {
      toast.error('El trámite no tiene un correo electrónico registrado.');
      return;
    }

    const result = await Swal.fire({
      title: '¿Reenviar correo de cotización?',
      html: `Se enviará nuevamente el correo de cotización con los datos para pago al solicitante:<br/><strong style="color: #0284c7; font-size: 1.05rem; display: block; margin-top: 8px;">${tramite.correo}</strong>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0284c7',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, reenviar correo',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setResendingMail(true);
    try {
      const res = await api.post(`/tramites/${tramite.id}/cotizacion/reenviar`);
      toast.success(res.data?.message || `Correo de cotización reenviado exitosamente a ${tramite.correo}.`);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al reenviar el correo de cotización.');
    } finally {
      setResendingMail(false);
    }
  };

  const isViewingOldVersion = viewingVersionId && cotizacionesHistory.length > 0 && viewingVersionId !== cotizacionesHistory[0]?.id;
  const isReadOnly = isViewingOldVersion || (isQuoteLocked && !isGeneratingNew);

  const loadCotizacionToState = (cot) => {
    setSelectedCajaId(cot.usuario_caja_id || '');
    setObservaciones(cot.observaciones || '');
    
    if (cot.items && cot.items.length > 0) {
      const savedItems = cot.items.filter(i => !i.is_cuantia && i.descripcion !== 'OTROS');
      const savedCuantias = cot.items.filter(i => i.is_cuantia);

      const newItems = DEFAULT_ITEMS.map(desc => {
        const saved = savedItems.find(si => {
          if (si.is_custom) return false;
          if (si.descripcion === desc) return true;
          if (desc === 'CERTIFICACIÓN DIGITAL' && (si.descripcion === 'FIRMA ELECTRÓNICA' || si.descripcion?.toUpperCase() === 'FIRMA ELECTRÓNICA')) {
            return true;
          }
          return false;
        });
        return {
          descripcion: desc,
          cantidad: saved ? saved.cantidad : 1,
          precio_unitario: saved ? saved.precio_unitario : 0,
          is_custom: false,
          is_cuantia: false
        };
      });

      const customSaved = savedItems.filter(si => si.is_custom && !si.descripcion?.toLowerCase().includes('abono') && si.descripcion !== 'OTROS');
      setItems([...newItems, ...customSaved.map(si => ({
        descripcion: si.descripcion,
        cantidad: si.cantidad || 1,
        precio_unitario: si.precio_unitario || 0,
        is_custom: true,
        is_cuantia: false,
        descuento_monto: si.descuento_monto || 0,
        descuento_tipo: si.descuento_tipo || 'fijo',
        descuento_descripcion: si.descuento_descripcion || ''
      }))]);
      
      setDescuentoGlobalMonto(cot.descuento_monto || 0);
      setDescuentoGlobalTipo(cot.descuento_tipo || 'fijo');

      setCuantias(savedCuantias.map(sc => ({
        descripcion: sc.descripcion,
        cantidad: 1, 
        precio_unitario: sc.precio_unitario || sc.monto || 0,
        is_custom: false,
        is_cuantia: true,
        tipo_cuantia: sc.tipo_cuantia || ''
      })));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cajaRes, assignRes, cotRes] = await Promise.all([
          api.get('/cotizaciones/caja-users'),
          api.get('/users/assignable'),
          api.get(`/tramites/${tramite.id}/cotizacion/historial`).catch(() => null)
        ]);

        setCajaUsers(cajaRes.data);
        setAssignableUsers(assignRes.data.data || []);

        if (cotRes && cotRes.data && cotRes.data.length > 0) {
          setCotizacionesHistory(cotRes.data);
          const cot = cotRes.data[0]; // La más reciente
          setViewingVersionId(cot.id);
          loadCotizacionToState(cot);
        }
      } catch (err) {
        toast.error('Error al cargar datos de cotización.');
      } finally {
        setFetching(false);
      }
    };
    fetchData();
  }, [tramite.id, tramite.asignado_a]);

  const totalPagadoAnterior = useMemo(() => {
    if (!cotizacionesHistory || cotizacionesHistory.length === 0) return 0;
    return cotizacionesHistory
      .filter(h => h.estado === 'pagada')
      .reduce((acc, h) => acc + (parseFloat(h.total) || 0), 0);
  }, [cotizacionesHistory]);

  const total = useMemo(() => {
    if (isReadOnly) {
      const currentCot = cotizacionesHistory.find(h => h.id === viewingVersionId);
      if (currentCot && currentCot.total !== undefined && currentCot.total !== null) {
        return Math.ceil(parseFloat(currentCot.total));
      }
    }

    let sumItems = 0;
    items.forEach(i => {
      let sub = (parseInt(i.cantidad) || 0) * (parseInt(i.precio_unitario) || 0);
      let desc = parseInt(i.descuento_monto) || 0;
      if (i.descuento_tipo === 'porcentaje') {
        sub = Math.ceil(sub - (sub * (desc / 100)));
      } else if (i.descuento_tipo === 'fijo') {
        sub = sub - desc;
      }
      sumItems += Math.max(0, sub);
    });

    const totalCuantias = cuantias.reduce((acc, c) => acc + (parseInt(c.precio_unitario) || 0), 0);
    
    let subtotalFinal = sumItems + totalCuantias;
    
    // 1. Aplicar descuento global PRIMERO sobre el total de los ítems
    let descGlobal = parseInt(descuentoGlobalMonto) || 0;
    if (descuentoGlobalTipo === 'porcentaje') {
      subtotalFinal = Math.ceil(subtotalFinal - (subtotalFinal * (descGlobal / 100)));
    } else if (descuentoGlobalTipo === 'fijo') {
      subtotalFinal = subtotalFinal - descGlobal;
    }

    subtotalFinal = Math.max(0, subtotalFinal);

    // 2. Descontar abono de versiones pagadas sobre el valor ya rebajado
    if (totalPagadoAnterior > 0) {
      subtotalFinal = Math.max(0, subtotalFinal - totalPagadoAnterior);
    }

    return Math.ceil(subtotalFinal);
  }, [items, cuantias, descuentoGlobalMonto, descuentoGlobalTipo, isReadOnly, viewingVersionId, cotizacionesHistory, totalPagadoAnterior]);

  const handlePrecioChange = (idx, val) => {
    const newItems = [...items];
    newItems[idx].precio_unitario = val;
    setItems(newItems);
  };

  const handleCantidadChange = (idx, val) => {
    const newItems = [...items];
    newItems[idx].cantidad = val;
    setItems(newItems);
  };

  const handleDescripcionChange = (idx, val) => {
    const newItems = [...items];
    newItems[idx].descripcion = val;
    setItems(newItems);
  };

  const addCustomItem = () => {
    setItems([...items, { descripcion: '', cantidad: 1, precio_unitario: 0, is_custom: true, is_cuantia: false }]);
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const addCuantia = () => {
    setCuantias([...cuantias, { tipo_cuantia: '', descripcion: '', cantidad: 1, precio_unitario: 0, is_cuantia: true, is_custom: false }]);
  };

  const removeCuantia = (idx) => {
    setCuantias(cuantias.filter((_, i) => i !== idx));
  };

  const handleCuantiaChange = (idx, field, val) => {
    const newCuantias = [...cuantias];
    newCuantias[idx][field] = val;
    setCuantias(newCuantias);
  };

  const handleSubmit = async (enviar = false) => {
    if (!selectedCajaId) {
      toast.error('Debe seleccionar un cajero (Caja).');
      return;
    }

    // Filtrar ítems con precio_unitario > 0 o que sean custom (excluyendo 'OTROS')
    const filteredItems = items.filter(i => (i.precio_unitario > 0 || i.is_custom) && i.descripcion !== 'OTROS');
    const filteredCuantias = cuantias.filter(c => c.tipo_cuantia && c.descripcion);

    if (filteredItems.length === 0 && filteredCuantias.length === 0) {
      toast.error('Debe ingresar al menos un ítem o cuantía con valor.');
      return;
    }

    const payloadItems = [...filteredItems, ...filteredCuantias];

    setLoading(true);
    try {
      await api.post(`/tramites/${tramite.id}/cotizacion`, {
        usuario_caja_id: selectedCajaId,
        asignado_a: selectedAssigneeId,
        total,
        observaciones,
        items: payloadItems,
        enviar,
        descuento_monto: parseInt(descuentoGlobalMonto) || 0,
        descuento_tipo: descuentoGlobalTipo
      });

      toast.success(enviar ? 'Cotización enviada y trámite asignado.' : 'Cotización guardada.');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al procesar la cotización.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    handleImprimirCotizacionTramite(tramite, {
      items,
      cuantias,
      total,
      observaciones,
      descuentoMonto: parseInt(descuentoGlobalMonto) || 0,
      descuentoDescripcion: descuentoGlobalTipo,
      repertorios: tramite.repertorios || []
    });
  };

  if (fetching) return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ textAlign: 'center', padding: '3rem' }}>
        <span className="spinner spinner-lg" />
        <p style={{ marginTop: '1rem' }}>Cargando datos...</p>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 800, width: '95%', padding: 0 }} onClick={e => e.stopPropagation()}>
        
        <div className="modal-header" style={{ padding: '1.5rem 2rem', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-color)' }}>
          <h2 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
            <DollarSign size={24} className="text-copper" /> 
            Cotización de Trámite: {tramite.numero_seguimiento}
            {isViewingOldVersion && <span className="badge badge-warning" style={{ fontSize: '0.8rem', marginLeft: 10 }}>Viendo versión antigua (Solo Lectura)</span>}
          </h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {cotizacionesHistory.length > 1 && (
              <button 
                type="button"
                className="btn btn-sm no-print" 
                style={{ background: '#4f46e5', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                onClick={() => setShowCompareModal(true)} 
                title="Comparar versiones de esta cotización"
              >
                <Layers size={14} /> Comparar Versiones ({cotizacionesHistory.length})
              </button>
            )}
            {hasExistingQuote && (
              <button 
                type="button"
                className="btn btn-sm no-print" 
                style={{ 
                  background: '#0284c7', 
                  color: '#ffffff', 
                  border: 'none', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  fontWeight: 600,
                  padding: '6px 12px',
                  borderRadius: 6,
                  cursor: resendingMail ? 'not-allowed' : 'pointer'
                }}
                onClick={handleReenviarCorreo} 
                disabled={resendingMail || loading}
                title={`Reenviar correo de cotización a ${tramite.correo || 'el solicitante'}`}
              >
                {resendingMail ? <span className="spinner" style={{ width: 14, height: 14 }} /> : <Mail size={15} />}
                Reenviar Correo
              </button>
            )}
            <button className="btn btn-secondary btn-sm no-print" onClick={handlePrint} title="Imprimir Cotización">
              <Printer size={16} /> Imprimir
            </button>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div style={{ maxHeight: '75vh', overflowY: 'auto', padding: '2rem' }}>
          
          <div className="no-print" style={{ marginBottom: '2rem', padding: '1rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <h5 style={{ margin: '0 0 0.75rem 0', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--primary-dark)' }}>
              <Info size={16} /> Información de Repertorios
            </h5>
            {tramite.exento_repertorio ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#ecfdf5', border: '1px solid #10b981', padding: '10px 14px', borderRadius: '8px' }}>
                <span style={{ fontSize: '1.25rem' }}>🛡️</span>
                <div>
                  <div style={{ fontWeight: 700, color: '#065f46', fontSize: '0.9rem' }}>
                    Trámite Exento de Repertorio Previo
                  </div>
                  <div style={{ color: '#047857', fontSize: '0.8rem' }}>
                    {tramite.motivo_exento_repertorio ? `Motivo: ${tramite.motivo_exento_repertorio}. ` : ''}
                    Este trámite está institucionalmente autorizado para cotizar y procesar sin asociar números de repertorio (ej. Alzamientos o Cancelaciones).
                  </div>
                </div>
              </div>
            ) : tramite.repertorios && tramite.repertorios.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                {tramite.repertorios.map(r => (
                  <div key={r.id} style={{ fontSize: '0.85rem', padding: '10px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 800, color: 'var(--copper)', fontSize: '0.95rem' }}>#{r.numero_repertorio}</span>
                      <span style={{ fontSize: '0.72rem', background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
                        {r.contrato?.nombre === 'Otro' ? r.contrato_otro : (r.contrato?.nombre || 'Acto Registral')}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{r.nombre_requirente}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--error)', fontStyle: 'italic' }}>
                No hay repertorios asignados. Se recomienda asignarlos primero o activar "No requiere repertorio" desde el modal de repertorios si corresponde a un acto exento.
              </p>
            )}
          </div>

          {cotizacionesHistory.length > 1 && (
            <div className="no-print" style={{ marginBottom: '2rem', padding: '1rem', background: '#f8fafc', borderRadius: 8, border: '1px solid var(--border-color)' }}>
              <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: 'var(--primary-dark)' }}>Historial de Versiones</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {cotizacionesHistory.map((h, i) => (
                  <li key={h.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'white', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.9rem' }}>
                    <div>
                      <strong style={{ color: 'var(--copper)' }}>Versión {cotizacionesHistory.length - i}</strong> 
                      {i === 0 && <span className="badge badge-info" style={{ marginLeft: 8, fontSize: '0.7rem' }}>Actual</span>}
                      <br/>
                      <span style={{ color: 'var(--gray-500)', fontSize: '0.8rem' }}>{new Date(h.created_at).toLocaleString('es-CL')}</span>
                    </div>
                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                      <div>
                        <span className={`badge ${h.estado === 'pagada' ? 'badge-success' : (h.estado === 'borrador' ? 'badge-warning' : 'badge-primary')}`} style={{ marginRight: 10 }}>
                          {h.estado}
                        </span>
                        <strong>${new Intl.NumberFormat('es-CL').format(h.total)}</strong>
                      </div>
                      <button 
                        className="btn btn-sm" 
                        style={{ padding: '2px 8px', fontSize: '0.75rem', background: viewingVersionId === h.id ? 'var(--bg-body)' : 'white', border: '1px solid var(--border-color)', color: viewingVersionId === h.id ? 'var(--primary)' : 'var(--text-muted)' }}
                        onClick={() => {
                          setViewingVersionId(h.id);
                          loadCotizacionToState(h);
                          setIsGeneratingNew(false);
                          toast.success(`Cargada Versión ${cotizacionesHistory.length - i} para visualizar.`);
                        }}
                        disabled={viewingVersionId === h.id}
                      >
                        {viewingVersionId === h.id ? 'Visualizando' : 'Ver Detalles'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
            <div className="form-group">
              <label style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                <User size={16} /> Designación (Caja) *
              </label>
              <select 
                value={selectedCajaId} 
                onChange={e => setSelectedCajaId(e.target.value)}
                style={{ borderRadius: 8, padding: '10px 12px' }}
                disabled={isFinalized}
              >
                <option value="">Seleccione Cajero...</option>
                {cajaUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 4 }}>
                Usuario encargado del cobro y validación.
              </p>
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle size={16} /> Asignar a Funcionario (Opcional)
              </label>
              <select 
                value={selectedAssigneeId} 
                onChange={e => setSelectedAssigneeId(e.target.value)}
                style={{ borderRadius: 8, padding: '10px 12px' }}
                disabled={isFinalized}
              >
                <option value="">Seleccione Funcionario...</option>
                {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 4 }}>
                Funcionario que procesará el trámite tras el pago.
              </p>
            </div>
          </div>

          <div className="print-section">
            <h4 style={{ fontSize: '1rem', marginBottom: '1rem', color: 'var(--primary-dark)', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
              Detalle de Cobros - Trámite {tramite.numero_seguimiento}
            </h4>

            <ItemsCotizacionSection
              items={items}
              tramite={tramite}
              isReadOnly={isReadOnly}
              onDescripcionChange={handleDescripcionChange}
              onCantidadChange={handleCantidadChange}
              onPrecioChange={handlePrecioChange}
              onRemoveItem={removeItem}
              onAddCustomItem={addCustomItem}
            />

            <CuantiasCotizacionSection
              cuantias={cuantias}
              isReadOnly={isReadOnly}
              onCuantiaChange={handleCuantiaChange}
              onRemoveCuantia={removeCuantia}
              onAddCuantia={addCuantia}
            />

            {[...items, ...cuantias].filter(i => (parseInt(i.cantidad) || 0) > 0 && (parseInt(i.precio_unitario) || 0) > 0).length > 0 && (
              <div className="print-section" style={{ marginTop: '2rem', padding: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <h5 style={{ margin: '0 0 1rem 0', color: 'var(--primary-dark)', fontSize: '1rem' }}>Resumen de la Cotización</h5>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                      <th style={{ textAlign: 'left', padding: '8px', color: 'var(--gray-600)' }}>Documento / Ítem</th>
                      <th style={{ textAlign: 'center', padding: '8px', color: 'var(--gray-600)' }}>Cantidad</th>
                      <th style={{ textAlign: 'right', padding: '8px', color: 'var(--gray-600)' }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...items, ...cuantias].filter(i => (parseInt(i.cantidad) || 0) > 0 && (parseInt(i.precio_unitario) || 0) > 0 && i.descripcion !== 'OTROS').map((item, idx) => {
                      const subtotal = (parseInt(item.cantidad) || 0) * (parseInt(item.precio_unitario) || 0);
                      const displayDescripcion = (item.descripcion === 'FIRMA ELECTRÓNICA' || item.descripcion?.toUpperCase() === 'FIRMA ELECTRÓNICA')
                        ? 'CERTIFICACIÓN DIGITAL'
                        : item.descripcion;
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px', fontWeight: 500, color: 'var(--gray-800)' }}>
                            {item.is_cuantia && <span style={{fontSize: '0.75rem', background: 'var(--primary-light)', color: '#ffffff', padding: '2px 6px', borderRadius: 4, marginRight: 6}}>{item.tipo_cuantia}</span>}
                            {displayDescripcion}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{item.cantidad}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: 'var(--primary)' }}>${new Intl.NumberFormat('es-CL').format(subtotal)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ 
              marginTop: '2rem',
              padding: '1.5rem', 
              background: 'var(--bg-body)', 
              borderRadius: 12, 
              border: '2px solid var(--primary)',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase' }}>Descuento Global</div>
                  {!isReadOnly && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <select 
                        value={descuentoGlobalTipo} 
                        onChange={e => setDescuentoGlobalTipo(e.target.value)}
                        style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      >
                        <option value="fijo">Monto Fijo ($)</option>
                        <option value="porcentaje">Porcentaje (%)</option>
                      </select>
                      <input 
                        type="number"
                        min="0"
                        placeholder="0"
                        value={descuentoGlobalMonto}
                        onChange={e => setDescuentoGlobalMonto(e.target.value)}
                        style={{ padding: '6px', width: '80px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                      />
                    </div>
                  )}
                  {isReadOnly && (descuentoGlobalMonto > 0) && (
                    <span style={{ fontSize: '0.9rem', color: 'var(--primary)', fontWeight: 600 }}>
                      {descuentoGlobalTipo === 'porcentaje' ? `${descuentoGlobalMonto}%` : `$${new Intl.NumberFormat('es-CL').format(descuentoGlobalMonto)}`}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed var(--border-color)', paddingTop: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase' }}>Total a Pagar</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--primary)' }}>
                    ${new Intl.NumberFormat('es-CL').format(total)}
                  </div>
                  {totalPagadoAnterior > 0 && !isReadOnly && (
                    <div style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 600, marginTop: 4 }}>
                      ✓ Descuenta abono de ${new Intl.NumberFormat('es-CL').format(totalPagadoAnterior)} por versión anterior pagada
                    </div>
                  )}
                </div>
              <div style={{ textAlign: 'right', fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                <Info size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Solo se incluirán los ítems con valor mayor a 0.
              </div>
            </div>
          </div>
        </div>

          <div className="no-print form-group" style={{ marginTop: '1.5rem' }}>
            <label style={{ fontWeight: 700 }}>Observaciones adicionales</label>
            <textarea 
              rows={3} 
              value={observaciones} 
              onChange={e => setObservaciones(e.target.value)}
              placeholder="Notas para el cliente o el cajero..."
              style={{ borderRadius: 8 }}
              disabled={isReadOnly}
            />
          </div>
        </div>

        <div className="no-print" style={{ padding: '1rem 1.5rem', background: 'var(--bg-surface)', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={loading || resendingMail} style={{ padding: '6px 14px', fontSize: '0.85rem' }}>
            {isFinalized ? 'Cerrar' : 'Cancelar'}
          </button>

          {hasExistingQuote && (
            <button 
              type="button"
              className="btn btn-sm btn-outline"
              onClick={handleReenviarCorreo} 
              disabled={loading || resendingMail}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                color: '#0284c7', 
                borderColor: '#7dd3fc', 
                background: '#f0f9ff',
                fontWeight: 600, 
                padding: '6px 14px', 
                fontSize: '0.85rem',
                borderRadius: '6px' 
              }}
              title={`Reenviar correo de cotización a ${tramite.correo || 'el solicitante'}`}
            >
              {resendingMail ? <span className="spinner spinner-sm" style={{ width: 14, height: 14 }} /> : <Mail size={15} />}
              Reenviar Correo
            </button>
          )}

          {isViewingOldVersion && (
            <button 
              className="btn btn-primary btn-sm"
              onClick={() => {
                setViewingVersionId(cotizacionesHistory[0].id);
                loadCotizacionToState(cotizacionesHistory[0]);
                toast.success("Volviste a la versión actual.");
              }}
              style={{ padding: '6px 14px', fontSize: '0.85rem' }}
            >
              Volver a la Versión Actual
            </button>
          )}
          
          {originalStateLocked && tramite.estado?.nombre !== 'Completado' && !isGeneratingNew && !isViewingOldVersion && (
            <button 
              className="btn btn-sm" 
              style={{ background: 'var(--info)', color: 'white', border: 'none', padding: '6px 14px', fontSize: '0.85rem' }}
              onClick={() => {
                setIsGeneratingNew(true);
                toast.success("Formulario desbloqueado para crear una nueva versión.");
              }}
            >
              <Plus size={15} /> Crear Nueva Versión
            </button>
          )}
          {!isFinalized && !isViewingOldVersion && (
            <>
              <button 
                className="btn btn-sm" 
                style={{ border: '1px solid var(--primary)', color: 'var(--primary)', background: 'transparent', padding: '6px 14px', fontSize: '0.85rem' }}
                onClick={() => handleSubmit(false)} 
                disabled={loading}
              >
                {loading ? <span className="spinner" /> : (isQuoteLocked ? 'Guardar Cambios' : 'Guardar Borrador')}
              </button>
              {!isQuoteLocked && (
                <button 
                  className="btn btn-primary btn-sm" 
                  onClick={() => handleSubmit(true)} 
                  disabled={loading}
                  style={{ padding: '6px 16px', fontSize: '0.85rem' }}
                >
                  {loading ? <span className="spinner" /> : <><Send size={15} /> Guardar y Enviar al Cliente</>}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showCompareModal && (
        <HistorialCotizacionesModal
          isOpen={showCompareModal}
          tramite={tramite}
          onClose={() => setShowCompareModal(false)}
        />
      )}
    </div>
  );
}
